/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { isIE, isEdge } from "dc-preview-utils/src/Utils/UserAgentUtils";
import * as constants from "constants/constants";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getScrollBarSize } from "utils/DomUtils";

export const getMaxZoomFromAllPages = data => {
  const { pages: geometries, numPages } = data;
  let maxZoom = 99999;
  if (geometries.length < numPages) {
    maxZoom = geometries[0].maxZoom;
  } else {
    geometries.forEach(curr => {
      maxZoom = Math.min(curr.maxZoom, maxZoom);
    });
  }
  return maxZoom;
};

export const getMinZoomFromAllPages = data => {
  const { pages: geometries, numPages } = data;
  let minZoom = -1;
  if (geometries.length < numPages) {
    minZoom = geometries[0].minZoom;
  } else {
    geometries.forEach(curr => {
      minZoom = Math.max(curr.minZoom, minZoom);
    });
  }
  return minZoom;
};

export const calculateMaxZoomForIEEdge = (useReactVirtualized, data) => {
  if (useReactVirtualized || (!isIE() && !isEdge())) return Infinity;
  const geometries = data.pages;
  const numPages = data.numPages;
  const totalPageMargin = numPages * constants.PAGE_BOTTOM_MARGIN;
  let pagesHeight;
  if (geometries.length !== numPages) {
    pagesHeight = numPages * geometries[0].height;
  } else {
    pagesHeight = geometries.reduce((acc, item) => acc + item.height, 0);
  }
  let allowedMaxZoom = (1500000 - totalPageMargin) / pagesHeight;
  return (parseFloat(allowedMaxZoom.toFixed(2)) - 0.01) * getDevicePixelRatio();
};

export const getTruncatedZoomLevel = (minZoom, maxZoom, zoomLevel) => {
  const truncatedZoomLevel = Math.min(Math.max(minZoom, zoomLevel), maxZoom);
  return truncatedZoomLevel;
};

export const calculateZoomLevelAccToCurrPageRasterSize = (dcAPIRenditionSize, data, currPageIndex) => {
  currPageIndex = currPageIndex >= 0 ? currPageIndex : 0;
  const pagesGeometry = data.pages;
  const maxZoom = getMaxZoomFromAllPages(data);
  let zoomLevel = constants.DEFAULT_ZOOM_LEVEL;
  const width = pagesGeometry[currPageIndex]
    ? parseFloat(pagesGeometry[currPageIndex].width)
    : parseFloat(pagesGeometry[0].width);

  const height = pagesGeometry[currPageIndex]
    ? parseFloat(pagesGeometry[currPageIndex].height)
    : parseFloat(pagesGeometry[0].height);

  const heightZoomLevel = dcAPIRenditionSize / height;
  const widthZoomLevel = dcAPIRenditionSize / width;
  zoomLevel = Math.min(heightZoomLevel, widthZoomLevel, maxZoom);

  return zoomLevel;
};

export const getMinMaxZoomLimits = (useReactVirtualized, data) => {
  const maxZoom = getMaxZoomFromAllPages(data);
  const minZoom = getMinZoomFromAllPages(data);
  const allowedMaxZoom = calculateMaxZoomForIEEdge(useReactVirtualized, data);
  let docMinZoom = constants.DEFAULT_MIN_ZOOM * getDevicePixelRatio();
  let docMaxZoom = constants.DEFAULT_MAX_ZOOM * getDevicePixelRatio();
  docMinZoom = minZoom ? Math.max(docMinZoom, minZoom) : docMinZoom;
  docMaxZoom = maxZoom ? Math.min(docMaxZoom, maxZoom, allowedMaxZoom) : docMaxZoom;
  return {
    minZoom: docMinZoom,
    maxZoom: docMaxZoom
  };
};

export const getMarginsDiff = () => constants.PAGES_VIEW_MARGIN_LEFT + constants.PAGES_VIEW_MARGIN_RIGHT;

export const getMinZoomFromDefaultAndMaxZoom = (defaultZoomLevel, maxZoom) =>
  Math.min(defaultZoomLevel, parseFloat(maxZoom) / getDevicePixelRatio());

/*
 * TODO : Merge with getFitWidthZoomLevel in Zoom Stores
 */
export const getFitWidthZoomLevel = (availableWidth, options) => {
  const maxPageWidth = options.maxPageWidth;
  if (!maxPageWidth) {
    return constants.DEFAULT_ZOOM_LEVEL;
  }
  let availableViewWidth = availableWidth - getMarginsDiff();
  return availableViewWidth / maxPageWidth;
};

/*
 * TODO : Merge with getDefaultZoomLevel in ZoomUtils.js
 */
export const getDefaultZoomLevel = options => {
  // let availableWidth = getViewPortWidthWithoutPagesViewScrollbar(stores);
  let availableWidth = options.renditionContainerWidth - getScrollBarSize();
  if (options.fileShareType === "TRACK") {
    availableWidth *= 0.7;
    return Math.max(getFitWidthZoomLevel(availableWidth, options), constants.DEFAULT_ZOOM_LEVEL);
  }
  return getFitWidthZoomLevel(availableWidth, options);
};

/* Below function is a duplicate mix of of this._getInitialPageZoomLevel, getPageZoomLevel, getCurrPageZoomLevel, getNewPageZoomLevel
 * This has been written as we don't have access to stores in preview provider utils
 * TODO : Need to remove this and use same functions everywhere
 */
export const _getInitialPageZoomLevel = (data, currPageIndex) => {
  const minMaxZoomLevels = getMinMaxZoomLimits(true, data);
  const minZoom = minMaxZoomLevels.minZoom / getDevicePixelRatio();
  const maxZoom = minMaxZoomLevels.maxZoom / getDevicePixelRatio();
  let initialPageZoomLevel = getTruncatedZoomLevel(minZoom, maxZoom, constants.DEFAULT_ZOOM_LEVEL);
  // Currently above value is overriden by calculateZoomLevelAccToCurrPageRasterSize
  // TODO : Below function should only run if DCAPI is available. Assuming for now that DCAPI is available
  initialPageZoomLevel = calculateZoomLevelAccToCurrPageRasterSize(constants.DEFAULT_PAGE_SIZE, data, currPageIndex);
  return { ...minMaxZoomLevels, initialPageZoomLevel };
};

export const getMaxWidthFromPagesData = data => {
  const pages = data.pages;
  let maxWidth = 0;
  pages.forEach(currPage => {
    maxWidth = Math.max(maxWidth, currPage.width);
  });
  return maxWidth;
};

export const calculateZoomLevel = (data, options) => {
  const initialZoomLevelObj = _getInitialPageZoomLevel(data, 0);
  const maxPageWidth = getMaxWidthFromPagesData(data);
  const newOptions = { ...initialZoomLevelObj, ...options, maxPageWidth };
  const defaultZoomLevel = getDefaultZoomLevel(newOptions);
  let initialZoomLevel = newOptions.initialPageZoomLevel;
  if (newOptions.maxZoom) {
    initialZoomLevel = getMinZoomFromDefaultAndMaxZoom(defaultZoomLevel, newOptions.maxZoom);
  }
  return initialZoomLevel;
};

export const calculateEffectivePageWidth = options => {
  let availableWidth = options.renditionContainerWidth - getScrollBarSize();
  return availableWidth - getMarginsDiff();
};
